/**
 * Projects — show view on the shared Inspector grammar (coverage §2.1).
 * Header / Overview / Relations / Audit. Relations: parent Company (RefLink up)
 * + child Artifacts / Documents / Objectives / Decisions (filtered
 * back-reference reads, each degrading to a calm "None." when empty).
 *
 * Composes the lower-level Inspector pieces directly so the back-reference
 * `useList` hooks run unconditionally at the top of a real component.
 */
import {
  useShow,
  useGo,
  useNavigation,
  useList,
  type HttpError,
} from "@refinedev/core";
import {
  Pencil,
  Building2,
  Boxes,
  FileText,
  Target,
  GitBranch,
} from "@/components/ui/icon";
import type {
  Project,
  Artifact,
  Document,
  Objective,
  Decision,
} from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/app/MarkdownView";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  FieldGrid,
  Field,
  RefLink,
  RelationsBlock,
  AuditBlock,
  type RelationItem,
} from "@/components/inspector";

/** Filtered back-reference list (degrades silently to []). */
function useChildren<T extends { id: string }>(
  resource: string,
  field: string,
  value: string | undefined,
) {
  const { data } = useList<T, HttpError>({
    resource,
    filters: value ? [{ field, operator: "eq", value }] : [],
    pagination: { pageSize: 25 },
    queryOptions: { enabled: Boolean(value), retry: false },
    successNotification: false,
    errorNotification: false,
  });
  return data?.data ?? [];
}

export function ProjectShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<Project, HttpError>({ resource: "projects" });
  const { data, isLoading, isError, error, refetch } = query;
  const project = data?.data;
  const projectId = project?.id;

  const artifacts = useChildren<Artifact>("artifacts", "project_id", projectId);
  const documents = useChildren<Document>("documents", "project_id", projectId);
  const objectives = useChildren<Objective>(
    "objectives",
    "project_id",
    projectId,
  );
  const decisions = useChildren<Decision>("decisions", "project_id", projectId);

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !project) {
    return (
      <ShowPageError
        message={error?.message}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const childItems = (
    label: string,
    icon: RelationItem["icon"],
    resource: string,
    rows: { id: string; title?: string; name?: string }[],
  ): RelationItem[] =>
    rows.map((r) => ({
      key: `${resource}:${r.id}`,
      label,
      icon,
      node: <RefLink resource={resource} id={r.id} label={r.title ?? r.name} />,
    }));

  return (
    <Inspector>
      <InspectorHeader
        title={project.name}
        subtitle="A construction or development project — its company, artifacts, and team."
        status={project.status}
        primaryAction={
          <Button
            variant="outline"
            onClick={() => go({ to: editUrl("projects", project.id) })}
          >
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        }
        menuActions={[
          {
            label: "New artifact here",
            onClick: () =>
              go({
                to: { resource: "artifacts", action: "create" },
                query: { project_id: project.id },
              }),
          },
        ]}
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Description" full>
            {project.description ? (
              <Markdown source={project.description} />
            ) : null}
          </Field>
          <Field label="Company">
            <RefLink resource="companies" id={project.company_id} />
          </Field>
          <Field label="Created">
            {new Date(project.created_at).toLocaleString()}
          </Field>
          <Field label="Updated">
            {new Date(project.updated_at).toLocaleString()}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              items: [
                {
                  key: "company",
                  label: "Company",
                  icon: Building2,
                  node: (
                    <RefLink resource="companies" id={project.company_id} />
                  ),
                },
              ],
            },
            {
              title: "Artifacts",
              emptyMessage: "No artifacts in this project yet.",
              items: childItems("Artifact", Boxes, "artifacts", artifacts),
            },
            {
              title: "Documents",
              emptyMessage: "No documents in this project yet.",
              items: childItems("Document", FileText, "documents", documents),
            },
            {
              title: "Objectives",
              emptyMessage: "No objectives in this project yet.",
              items: childItems("Objective", Target, "objectives", objectives),
            },
            {
              title: "Decisions",
              emptyMessage: "No decisions in this project yet.",
              items: childItems("Decision", GitBranch, "decisions", decisions),
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={project.id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="projects" entityId={project.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default ProjectShow;
